// Servicio de autenticación para el nuevo backend FastAPI
import { getApiBaseUrl } from "@/lib/api/apiClient";

export interface LoginCredentials {
  correo: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  user?: {
    ownerID: string;
    userName: string;
    name: string;
    correo: string;
    acl: string;
    region?: string;
    puesto?: string;
  };
  token?: string;
  error?: string;
}

const LOGIN_TIMEOUT_MS = 25000; // 25 segundos
const HEALTH_CHECK_TIMEOUT_MS = 5000; // 5 segundos

/**
 * Comprueba si el backend responde (endpoint ligero sin base de datos).
 * Útil para distinguir "servidor no está en marcha" de "login lento o fallido".
 */
export async function checkApiHealth(): Promise<boolean> {
  const base = getApiBaseUrl();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(`${base}/health`, { signal: controller.signal });
    clearTimeout(timeoutId);
    return res.ok;
  } catch {
    clearTimeout(timeoutId);
    return false;
  }
}

// Codifica la contraseña para ocultar en Network (el backend la decodifica)
function encodePassword(password: string): string {
  return btoa(unescape(encodeURIComponent(password)));
}

export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

  try {
    const API_BASE_URL = getApiBaseUrl();
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        correo: credentials.correo,
        password: encodePassword(credentials.password),
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      const token = data?.access_token;
      if (!token) {
        return {
          success: false,
          error: "El servidor no devolvió un token. Intenta de nuevo.",
        };
      }
      if (typeof document !== "undefined") {
        const isHttps = window.location.protocol === "https:";
        const secureFlag = isHttps ? "; Secure" : "";
        document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax${secureFlag}`;
      }
      if (data?.user) {
        try {
          localStorage.setItem("currentUser", JSON.stringify(data.user));
        } catch {
          // localStorage no disponible (Safari privado); la autenticación sigue funcionando vía cookie
        }
      }
      return {
        success: true,
        user: data.user,
        token,
      };
    } else {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.detail || "Error de autenticación"
      };
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      return {
        success: false,
        error: "El servidor no respondió a tiempo. Comprueba que el backend esté en ejecución (puerto 8000) y que PostgreSQL esté activo (puerto 5434). Reinicia con: docker compose restart backend",
      };
    }
    return {
      success: false,
      error: "Error de conexión con el servidor. Comprueba que el backend esté en ejecución.",
    };
  }
}

export async function register(userData: any): Promise<AuthResponse> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, user: data };
    } else {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.detail || "Error al crear usuario"
      };
    }
  } catch (error) {
    return {
      success: false,
      error: "Error de conexión con el servidor",
    };
  }
}

export async function logout() {
  const API_BASE_URL = getApiBaseUrl();
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Si falla la llamada al servidor, limpiar localmente de todos modos
  }
  try {
    localStorage.removeItem("currentUser");
  } catch {
    // Safari privado; ignorar
  }
  if (typeof document !== "undefined") {
    document.cookie = "auth_token=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
}

export function getCurrentUser() {
  if (typeof window !== 'undefined') {
    try {
      const userStr = localStorage.getItem("currentUser");
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }
  return null;
}
